<script lang="ts">
    import { onMount } from 'svelte';
    import { push } from 'svelte-spa-router';
    import Table from '../../Table.svelte';
    import SearchInput from '../../SearchInput.svelte';
    import DateRange from '../../DateRange.svelte';
    import Button from '../../Button.svelte';
    import StatusBadge from '../../StatusBadge.svelte';
    import Notification from '../../Notification.svelte';
    import BackButton from '../../BackButton.svelte';
    import employerEmployeeService from '../../../lib/api/employer/employees';

    // Get the employee ID from the route params
    export let employeeId: string = '';

    console.log("employeeId", employeeId);

    // State for employee data
    let employee: any = null;
    let employeeLeaves: any[] = [];
    let loading = false;
    let error: string | null = null;

    // Filter donation requests into Pending Requests (only pending) and Processed Requests (all except pending)
    $: leaveRequests = employeeLeaves.filter(leave => 
        leave.status === 'Pending'
    );
    
    $: approvedRequests = employeeLeaves.filter(leave => 
        leave.status !== 'Pending'
    );

    // Filter state - commented out
    // let searchPhrase = '';
    // let startDate = '';
    // let endDate = '';

    // Notification state
    let notificationShow = false;
    let notificationType: 'success' | 'error' = 'success';
    let notificationMessage = '';

    function showNotification(message: string, type: 'success' | 'error' = 'success') {
        notificationMessage = message;
        notificationType = type;
        notificationShow = true;
        setTimeout(() => notificationShow = false, 4000);
    }

    // Function to load employee details
    async function loadEmployeeDetails() {
        if (!employeeId) return;
        
        loading = true;
        error = null;

        try {
            // Get employee details from API
            const employeeData = await employerEmployeeService.getEmployeesDetail(employeeId);
            
            // Transform employee data
            employee = {
                id: employeeData.uuid,
                name: employeeData.full_name,
                email: employeeData.email,
                phone: employeeData.phone,
                position: employeeData.job_title || 'N/A',
                location: employeeData.location || 'N/A',
                status: employeeData.user_status?.name || 'Active',
                totalLeaveHours: employeeData.credit_total || 0,
                totalDonatedHours: employeeData.total_donated_hours || 0,
                totalDonationAmount: employeeData.total_donation_amount || 0,
                totalPendingHours: employeeData.total_pending_hours || 0,
                lastDonationDate: employeeData.last_donation_request_date
            };

            // Transform donation requests data
            employeeLeaves = employeeData?.donation_requests?.map((request: {
                uuid: string;
                created_at: string;
                leave_hours: number;
                donation_request_status?: { name: string };
                charity: { name: string };
                total_donation?: number;
                employer_comment?: string;
                rejection_reason?: string;
                employer_top_up?: number;
            }) => {
                const status = request.donation_request_status?.name || 'Pending';
                const statusMap: Record<string, string> = {
                    'Processed': 'status-approved',
                    'Approved': 'status-approved',
                    'Pending': 'status-pending',
                    'Rejected': 'status-rejected'
                };

                const statusIcons: Record<string, string> = {
                    'Processed': `<svg class="status-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>`,
                    'Approved': `<svg class="status-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>`,
                    'Pending': `<svg class="status-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>`,
                    'Rejected': `<svg class="status-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`
                };

                return {
                    id: request.uuid,
                    uuid: request.uuid,
                    requestDate: request.created_at,
                    hours: request.leave_hours,
                    status,
                    statusClass: statusMap[status] || 'status-pending',
                    statusIcon: statusIcons[status] || statusIcons['Pending'],
                    charity: request.charity.name,
                    amount: request.total_donation,
                    notes: request.employer_comment || request.rejection_reason || '-',
                    employer_top_up: request.employer_top_up || 0
                };
            }) || [];

            loading = false;
        } catch (err) {
            console.error('Error loading employee details:', err);
            error = 'Failed to load employee details';
            showNotification('Failed to load employee details', 'error');
            loading = false;
        }
    }

    // Function to handle search - commented out
    // function handleSearch() {
    //     const lowerPhrase = searchPhrase.toLowerCase();
    //     employeeLeaves = employeeLeaves.filter(leave => 
    //         leave.charity.toLowerCase().includes(lowerPhrase) ||
    //         leave.status.toLowerCase().includes(lowerPhrase)
    //     );
    // }

    // Function to handle date range change - commented out
    // function handleDateRangeChange(event: CustomEvent) {
    //     startDate = event.detail.startDate;
    //     endDate = event.detail.endDate;
    //     // Filter leaves by date range
    //     // This would typically be handled by the API
    // }

    // Function to go back
    function goBack() {
        push('#/employees');
    }

    // Function to handle row click - navigate to donation detail
    function handleRowClick(row: any) {
        if (row && (row.uuid || row.id)) {
            const donationId = row.uuid || row.id;
            push(`/view-leave-request?id=${donationId}`);
        }
    }

    // Table columns definition
    const columns = [
        {
            key: 'charity',
            label: 'Charity',
            width: '20%'
        },
        {
            key: 'requestDate',
            label: 'Date',
            width: '15%',
            render: (value: string) => {
                return new Date(value).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                });
            }
        },
        {
            key: 'hours',
            label: 'Hours',
            width: '10%',
            render: (value: number) => `${value} hrs`
        },
       
        {
            key: 'amount',
            label: 'Amount',
            width: '15%',
            render: (value: number) => new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            }).format(value)
        },
        {
            key: 'status',
            label: 'Status',
            width: '18%',
            align: 'center',
            render: (value: string, row: any) => {
                return `<span class="status-badge ${row.statusClass}">${row.statusIcon} ${value}</span>`;
            }
        },
        {
            key: 'notes',
            label: 'Notes',
            width: '20%'
        }
    ];

    onMount(() => {
        if (employeeId) {
            loadEmployeeDetails();
        }
    });
</script>

<div class="employee-view-page">
    {#if loading}
        <div class="loading-container">
            <div class="loading-spinner"></div>
            <p>Loading employee details...</p>
        </div>
    {:else if error}
        <div class="error-container">
            <p class="error-message">{error}</p>
            <Button variant="primary" on:click={loadEmployeeDetails}>Try Again</Button>
        </div>
    {:else if employee}
        <div class="employee-content">
            <!-- Back Button -->
            <div class="back-link">
                <BackButton text="Back" onClick={() => window.history.back()} />
            </div>

            <!-- Employee Information Section -->
            <section class="info-section employee-info-section">
                <div class="employee-info-content">
                    <div class="employee-header">
                        <div class="avatar avatar-blue">{employee.name[0]}</div>
                        <div class="employee-details">
                            <h3 class="employee-name">{employee.name}</h3>
                            <div class="employee-subinfo">
                                <span>{employee.position}</span>
                            </div>
                        </div>
                        <div class="status-badge-container">
                            <StatusBadge status={employee.status.toLowerCase()} />
                        </div>
                    </div>
                </div>
            </section>

            <!-- Employee Stats Section -->
            <section class="info-section">
                <div class="stats-grid">
                    <div class="stat-card">
                        <span class="stat-label">Email</span>
                        <span class="stat-value">{employee.email}</span>
                    </div>
                    <div class="stat-card">
                        <span class="stat-label">Phone</span>
                        <span class="stat-value">{employee.phone}</span>
                    </div>
                    <div class="stat-card">
                        <span class="stat-label">Location</span>
                        <span class="stat-value">{employee.location}</span>
                    </div>
                </div>
            </section>

            <!-- Pending Requests Section -->
            <section class="info-section">
                <h2 class="section-title">Pending Requests</h2>
                {#if leaveRequests.length === 0}
                    <div class="empty-state">
                        <p class="empty-message">No pending requests</p>
                    </div>
                {:else}
                    <div class="table-wrapper">
                        <Table
                            {columns}
                            data={leaveRequests}
                            onRowClick={handleRowClick}
                        />
                    </div>
                {/if}
            </section>

            <!-- Processed Requests Section -->
            <section class="info-section">
                <h2 class="section-title">Processed Requests</h2>
                {#if approvedRequests.length === 0}
                    <div class="empty-state">
                        <p class="empty-message">No processed requests yet</p>
                    </div>
                {:else}
                    <div class="table-wrapper">
                        <Table
                            {columns}
                            data={approvedRequests}
                            onRowClick={handleRowClick}
                        />
                    </div>
                {/if}
            </section>
        </div>
    {/if}
</div>

<Notification 
    bind:show={notificationShow}
    type={notificationType}
    message={notificationMessage}
/>

<style>
    .employee-view-page {
        padding: 1.5rem;
        background-color: #f7fafc;
        min-height: 100vh;
    }

    .employee-content {
        max-width: 1000px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        gap: 24px;
    }

    .back-link {
        margin-bottom: 0;
    }

    .loading-container, .error-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem;
        text-align: center;
        background: white;
        border-radius: 12px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        margin: 0 auto;
        max-width: 800px;
        width: 100%;
    }

    .loading-spinner {
        width: 2rem;
        height: 2rem;
        border: 3px solid #f3f4f6;
        border-top: 3px solid #f0b429;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin-bottom: 1rem;
    }

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    .error-message {
        color: #dc2626;
        margin-bottom: 1rem;
    }

    .info-section {
        background: white;
        border-radius: 12px;
        padding: 24px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        margin-top: 1rem;
    }

    .employee-header {
        display: flex;
        align-items: center;
        gap: 16px;
    }

    .avatar {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25rem;
        font-weight: 600;
        color: white;
    }

    .avatar-blue {
        background-color: #f0b429;
    }

    .employee-details {
        flex: 1;
    }

    .employee-name {
        font-size: 1.25rem;
        font-weight: 600;
        color: #111827;
        margin: 0;
    }

    .employee-subinfo {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #6B7280;
        font-size: 0.875rem;
        margin-top: 4px;
    }

    .section-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #111827;
        margin: 0 0 1.5rem 0;
    }

    .stats-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 1rem;
    }

    .stat-card {
        padding: 1rem;
        background: #f9fafb;
        border-radius: 8px;
    }

    .stat-label {
        font-size: 0.875rem;
        color: #6B7280;
        display: block;
        margin-bottom: 0.5rem;
    }

    .stat-value {
        font-size: 1.125rem;
        font-weight: 500;
        color: #111827;
    }

    .table-wrapper {
        overflow-x: auto;
    }

    .empty-state {
        padding: 2rem;
        text-align: center;
    }

    .empty-message {
        color: #6b7280;
        font-size: 0.875rem;
        margin: 0;
    }

    :global(.status-badge) {
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.025em;
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
    }

    :global(.status-icon) {
        width: 1rem;
        height: 1rem;
    }

    :global(.status-approved) {
        background-color: #DEF7EC;
        color: #03543F;
    }

    :global(.status-pending) {
        background-color: #FEF3C7;
        color: #92400E;
    }

    :global(.status-rejected) {
        background-color: #FEE2E2;
        color: #991B1B;
    }

    :global(.request-id) {
        font-family: monospace;
        font-size: 0.875rem;
        color: #4B5563;
    }

    @media (max-width: 768px) {
        .employee-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
        }

        .status-badge-container {
            margin-left: 0;
        }

    }
</style>
